/**
 * TenantStore - Synchronous tenant ID storage
 * 
 * This module provides a synchronous way to access the current tenant ID
 * for use in service files that cannot use React hooks.
 * 
 * The TenantProvider sets the tenant ID here on mount and auth changes,
 * and we also persist to localStorage as a fallback.
 */

let currentTenantId: string | null = null;

const STORAGE_KEY = 'current_tenant_id';

/**
 * Set the current tenant ID (called by TenantProvider)
 */
export function setTenantId(tenantId: string | null): void {
  currentTenantId = tenantId;
  
  if (tenantId) {
    localStorage.setItem(STORAGE_KEY, tenantId);
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
}

/**
 * Get the current tenant ID synchronously
 * 
 * This reads from the in-memory variable set by TenantProvider,
 * with a fallback to localStorage if the provider hasn't initialized yet.
 * 
 * @throws Error if no tenant ID is available
 */
export function getTenantIdSync(): string {
  if (currentTenantId) {
    return currentTenantId;
  }
  
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    return stored;
  }
  
  throw new Error('No tenant ID found. Please ensure you are logged in.');
}

/**
 * Check if a tenant ID is currently set
 */
export function hasTenantId(): boolean {
  return currentTenantId !== null || localStorage.getItem(STORAGE_KEY) !== null;
}

/**
 * Clear the tenant ID (called on logout)
 */
export function clearTenantId(): void {
  currentTenantId = null;
  localStorage.removeItem(STORAGE_KEY);
}
